import { useEffect, useRef } from 'react'
import { useSelector } from 'react-redux'
import { useRouter } from '@/i18n/navigation'
import { IRedux } from '../_store/types'
import { isTenantLocaleSettled } from '../_utils/shipper-switch'

/**
 * Home-page-level order type sync.
 * Soft-refreshes RSC when client order type diverges from SSR delivery_type,
 * or (on landing with no SSR type) when the client type changes after seed.
 *
 * Modeled on useSaveBranchData: change-gated, isRefreshingRef + rAF,
 * and isTenantLocaleSettled before router.refresh().
 */
export function useHomeOrderTypeSync(serverDeliveryType?: string) {
  const router = useRouter()
  const orderType = useSelector(
    (state: IRedux) => state.session.order_type?.type
  )
  const shipperId = useSelector(
    (state: IRedux) => state.settings?.data?.shipper_id
  )

  const isRefreshingRef = useRef(false)
  const seededRef = useRef(false)
  const lastTypeRef = useRef<string | undefined>(undefined)

  useEffect(() => {
    if (!orderType) return
    if (!shipperId || !isTenantLocaleSettled(shipperId)) return

    const softRefresh = () => {
      if (isRefreshingRef.current) return
      isRefreshingRef.current = true
      requestAnimationFrame(() => {
        router.refresh()
        setTimeout(() => {
          isRefreshingRef.current = false
        }, 100)
      })
    }

    // Catalog home: SSR menu is keyed by delivery_type cookie
    if (serverDeliveryType) {
      if (serverDeliveryType === orderType) {
        lastTypeRef.current = orderType
        seededRef.current = true
        return
      }
      lastTypeRef.current = orderType
      seededRef.current = true
      softRefresh()
      return
    }

    // Landing / no SSR delivery_type: refresh on type change after seed
    if (!seededRef.current) {
      seededRef.current = true
      lastTypeRef.current = orderType
      return
    }

    if (lastTypeRef.current === orderType) return
    lastTypeRef.current = orderType
    softRefresh()
  }, [orderType, serverDeliveryType, shipperId, router])
}
